import React, { useEffect, useState } from "react";
import { GraduationCap, Star, Mic, BookOpen, Trophy, Sparkles, Lock, Check, Users, Volume2, Loader2, Flame, Share2, Zap, MessagesSquare } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Topbar } from "@/components/Topbar";
import { PracticeDialog } from "@/components/PracticeDialog";
import { QuizDialog } from "@/components/QuizDialog";
import { RolePlay } from "@/components/RolePlay";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const DIFF_KEY = { beginner: "diffBeginner", intermediate: "diffIntermediate", advanced: "diffAdvanced" };

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div className="bg-white border border-border rounded-2xl p-5" data-testid={testid}>
      <div className="h-10 w-10 rounded-xl bg-accent/10 text-accent grid place-items-center mb-3"><Icon size={20} /></div>
      <div className="font-head font-bold text-3xl text-foreground">{value}</div>
      <div className="text-sm text-muted-foreground mt-0.5">{label}</div>
    </div>
  );
}

function PhraseRow({ p, onPractice, onToggleSave }) {
  const { t } = useI18n();
  const [playing, setPlaying] = useState(false);
  const arabic = p.arabic_text || p.translation;
  const english = p.english_text || p.original_text;
  const listen = async () => {
    if (playing) return;
    setPlaying(true);
    try {
      const { data } = await api.post("/tts", { text: arabic, lang: "ar" });
      const audio = new Audio(`data:audio/mp3;base64,${data.audio_base64}`);
      audio.onended = () => setPlaying(false);
      await audio.play();
    } catch { setPlaying(false); }
  };
  return (
    <div className="flex items-center justify-between gap-3 p-3 rounded-xl border border-border" data-testid={`phrase-row-${p.phrase_id}`}>
      <div className="min-w-0">
        <div className="font-semibold text-foreground truncate" dir="rtl">{arabic}</div>
        {p.transliteration && <div className="text-xs text-accent italic truncate">{p.transliteration}</div>}
        <div className="text-sm text-muted-foreground truncate">{english}</div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {p.mastered && <Badge className="bg-accent/10 text-accent hover:bg-accent/10 border-0"><Check size={12} className="me-1" />★</Badge>}
        <button onClick={listen} disabled={playing} title={t("listenLabel")} aria-label={t("listenLabel")} data-testid={`listen-${p.phrase_id}`} className="h-8 w-8 grid place-items-center rounded-full border border-border text-muted-foreground hover:bg-secondary">
          {playing ? <Loader2 size={14} className="animate-spin" /> : <Volume2 size={15} />}
        </button>
        <button onClick={() => onToggleSave(p)} title={p.saved ? t("savedLabel") : t("saveToMyPhrases")} aria-label={p.saved ? t("savedLabel") : t("saveToMyPhrases")} data-testid={`save-${p.phrase_id}`} className={`h-8 w-8 grid place-items-center rounded-full border ${p.saved ? "text-accent border-accent/30 bg-accent/10" : "text-muted-foreground border-border hover:bg-secondary"}`}>
          <Star size={15} className={p.saved ? "fill-current" : ""} />
        </button>
        <button onClick={() => onPractice(p)} title={t("practiceTitle")} aria-label={t("practiceTitle")} data-testid={`practice-${p.phrase_id}`} className="h-8 px-3 grid place-items-center rounded-full bg-primary text-white text-xs font-medium hover:bg-primary/90">
          <Mic size={14} />
        </button>
      </div>
    </div>
  );
}

function PackPhrase({ ph }) {
  const { t } = useI18n();
  const [playing, setPlaying] = useState(false);
  const listen = async () => {
    if (playing) return;
    setPlaying(true);
    try {
      const { data } = await api.post("/tts", { text: ph.translation, lang: "ar" });
      const audio = new Audio(`data:audio/mp3;base64,${data.audio_base64}`);
      audio.onended = () => setPlaying(false);
      await audio.play();
    } catch { setPlaying(false); }
  };
  return (
    <div className="flex items-center justify-between gap-3 py-2 border-b border-border/60 last:border-0">
      <div className="min-w-0">
        <div className="text-sm font-medium text-foreground truncate" dir="rtl">{ph.translation}</div>
        <div className="text-xs text-accent italic">{ph.transliteration}</div>
        <div className="text-xs text-muted-foreground truncate">{ph.original}</div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <Badge variant="secondary" className="text-[10px]">{t(DIFF_KEY[ph.difficulty] || "diffBeginner")}</Badge>
        <button onClick={listen} disabled={playing} className="h-7 w-7 grid place-items-center rounded-full border border-border hover:bg-secondary">
          {playing ? <Loader2 size={13} className="animate-spin" /> : <Volume2 size={13} />}
        </button>
      </div>
    </div>
  );
}

export default function Learning() {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isManager = user?.role === "manager";
  const [stats, setStats] = useState(null);
  const [phrases, setPhrases] = useState([]);
  const [packData, setPackData] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [practice, setPractice] = useState(null);
  const [quizOpen, setQuizOpen] = useState(false);
  const isPro = packData?.pro;

  const shareBadge = async () => {
    const text = `I've mastered ${stats?.mastered || 0} Arabic phrases on GulfBridge with a ${stats?.streak || 0}-day streak! 🎓`;
    try {
      if (navigator.share) await navigator.share({ title: "GulfBridge", text });
      else { await navigator.clipboard.writeText(text); toast.success(t("shareCopied")); }
    } catch { /* user cancelled */ }
  };

  const loadStats = () => api.get("/learning/stats").then(({ data }) => setStats(data)).catch((e) => console.error("Failed to load learning stats:", e));
  const loadPhrases = () => api.get("/learning/phrases/my").then(({ data }) => setPhrases(data)).catch((e) => console.error("Failed to load phrases:", e));
  const loadPacks = () => api.get("/learning/packs").then(({ data }) => setPackData(data)).catch((e) => console.error("Failed to load packs:", e));

  useEffect(() => { loadStats(); loadPhrases(); loadPacks(); }, []);

  const toggleSave = async (p) => {
    try {
      const { data } = await api.post(`/learning/phrases/${p.phrase_id}/save`);
      setPhrases((ps) => ps.map((x) => (x.phrase_id === p.phrase_id ? { ...x, saved: data.saved } : x)));
    } catch (e) { console.error("Failed to toggle save:", e); }
  };

  const startPractice = (p) => setPractice({ phrase_id: p.phrase_id, arabic: p.arabic_text || p.translation, translit: p.transliteration, english: p.english_text || p.original_text });

  const subscribe = async (pack) => {
    try {
      const { data } = await api.post(`/learning/packs/${pack.pack_id}/subscribe`);
      setPackData((pd) => ({ ...pd, packs: pd.packs.map((x) => (x.pack_id === pack.pack_id ? { ...x, subscribed: data.subscribed } : x)) }));
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const requirePack = async (pack) => {
    try {
      const { data } = await api.post(`/learning/packs/${pack.pack_id}/require`);
      setPackData((pd) => ({ ...pd, packs: pd.packs.map((x) => (x.pack_id === pack.pack_id ? { ...x, required: data.required } : x)) }));
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="min-h-screen bg-background">
      <Topbar />
      <div className="max-w-5xl mx-auto px-4 py-6">
        <h1 className="font-head font-bold text-2xl sm:text-3xl text-foreground mb-6 flex items-center gap-2">
          <GraduationCap className="text-accent" /> {t("learning")}
        </h1>

        <Tabs defaultValue="overview">
          <TabsList data-testid="learning-tabs">
            <TabsTrigger value="overview" data-testid="tab-overview"><Sparkles size={15} className="me-1.5" /> {t("learnOverview")}</TabsTrigger>
            <TabsTrigger value="phrases" data-testid="tab-my-phrases"><BookOpen size={15} className="me-1.5" /> {t("myPhrases")}</TabsTrigger>
            <TabsTrigger value="quiz" data-testid="tab-quiz"><Zap size={15} className="me-1.5" /> {t("quiz")}</TabsTrigger>
            <TabsTrigger value="roleplay" data-testid="tab-roleplay"><MessagesSquare size={15} className="me-1.5" /> {t("rolePlay")}</TabsTrigger>
            <TabsTrigger value="packs" data-testid="tab-content-packs"><Trophy size={15} className="me-1.5" /> {t("contentPacks")}</TabsTrigger>
          </TabsList>

          {/* Overview */}
          <TabsContent value="overview" className="mt-5 space-y-5">
            {stats && stats.total > 0 && (
              <div className="rounded-2xl bg-gradient-to-r from-primary to-accent p-5 text-white flex flex-col sm:flex-row sm:items-center gap-4" data-testid="digest-banner">
                <div className="flex-1">
                  <div className="font-head font-bold text-lg flex items-center gap-2"><Sparkles size={18} /> {t("digestTitle")}</div>
                  <div className="text-sm text-white/85 mt-1">{t("digestBody").replace("{n}", stats.top_week?.reduce((s, p) => s + (p.frequency || 0), 0) || 0)}</div>
                </div>
                <button onClick={() => setQuizOpen(true)} data-testid="digest-quiz-btn" className="h-11 px-5 rounded-xl bg-white text-primary font-semibold hover:bg-white/90 shrink-0 flex items-center gap-2"><Zap size={16} /> {t("startQuiz")}</button>
              </div>
            )}

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Stat icon={BookOpen} label={t("phrasesLearned")} value={stats?.total ?? 0} testid="stat-learned" />
              <Stat icon={Check} label={t("phrasesMastered")} value={stats?.mastered ?? 0} testid="stat-mastered" />
              <Stat icon={Flame} label={t("streakLabel")} value={stats?.streak ?? 0} testid="stat-streak" />
              <Stat icon={Star} label={t("phrasesSaved")} value={stats?.saved ?? 0} testid="stat-saved" />
            </div>

            {stats && stats.mastered > 0 && (
              <div className="rounded-2xl border border-accent/30 bg-accent/5 p-5 flex items-center gap-4" data-testid="achiever-badge">
                <div className="h-14 w-14 rounded-2xl bg-accent text-white grid place-items-center shrink-0"><Trophy size={26} /></div>
                <div className="flex-1 min-w-0">
                  <div className="font-head font-bold text-foreground">{t("badgeTitle")}</div>
                  <div className="text-sm text-muted-foreground"><b className="text-accent">{stats.mastered}</b> {t("badgeMasteredLabel")} · <Flame size={13} className="inline text-orange-500" /> {stats.streak} {t("streakLabel")}</div>
                </div>
                <button onClick={shareBadge} data-testid="share-badge-btn" className="h-10 px-4 rounded-xl border border-accent/40 text-accent font-medium hover:bg-accent/10 flex items-center gap-1.5 shrink-0"><Share2 size={15} /> {t("shareBadge")}</button>
              </div>
            )}

            {stats && stats.total === 0 && (
              <div className="bg-white border border-border rounded-2xl p-8 text-center text-muted-foreground text-sm" data-testid="learning-empty">{t("noPhrasesYet")}</div>
            )}

            <div className="grid lg:grid-cols-2 gap-4">
              {stats?.top_week?.length > 0 && (
                <div className="bg-white border border-border rounded-2xl p-5" data-testid="top-week">
                  <h3 className="font-head font-semibold text-foreground mb-3 flex items-center gap-2"><Sparkles size={16} className="text-accent" /> {t("topPhrasesWeek")}</h3>
                  <div className="space-y-2">
                    {stats.top_week.map((p) => (
                      <div key={p.phrase_id} className="flex items-center justify-between gap-3">
                        <div className="min-w-0"><span className="text-sm font-medium text-foreground" dir="rtl">{p.arabic_text || p.translation}</span> <span className="text-xs text-muted-foreground">· {p.english_text || p.original_text}</span></div>
                        <span className="text-xs font-semibold text-accent shrink-0">×{p.frequency}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {stats?.recently?.length > 0 && (
                <div className="bg-white border border-border rounded-2xl p-5" data-testid="recently-learned">
                  <h3 className="font-head font-semibold text-foreground mb-3 flex items-center gap-2"><BookOpen size={16} className="text-accent" /> {t("recentlyLearned")}</h3>
                  <div className="space-y-2">
                    {stats.recently.map((p) => (
                      <div key={p.phrase_id} className="flex items-center justify-between gap-3">
                        <div className="min-w-0 truncate"><span className="text-sm font-medium text-foreground" dir="rtl">{p.arabic_text || p.translation}</span></div>
                        {p.transliteration && <span className="text-xs text-accent italic shrink-0 truncate max-w-[45%]">{p.transliteration}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {stats?.leaderboard?.length > 0 && (
              <div className="bg-white border border-border rounded-2xl p-5" data-testid="leaderboard">
                <h3 className="font-head font-semibold text-foreground mb-4 flex items-center gap-2"><Trophy size={16} className="text-accent" /> {t("teamLeaderboard")}</h3>
                <div className="space-y-2">
                  {stats.leaderboard.map((a, i) => (
                    <div key={a.agent || i} className="flex items-center gap-3" data-testid={`leader-${i}`}>
                      <span className={`h-7 w-7 grid place-items-center rounded-full text-xs font-bold ${i === 0 ? "bg-accent text-white" : "bg-secondary text-foreground"}`}>{i + 1}</span>
                      <span className="font-medium text-foreground flex-1 truncate">{a.agent}</span>
                      <span className="text-sm text-muted-foreground">{a.phrases} {t("phrasesCount")}</span>
                      {a.mastered > 0 && <Badge className="bg-accent/10 text-accent hover:bg-accent/10 border-0 text-xs">{a.mastered} ★</Badge>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          {/* My Phrases */}
          <TabsContent value="phrases" className="mt-5">
            <div className="bg-white border border-border rounded-2xl p-5">
              {phrases.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-6">{t("noPhrasesYet")}</p>
              ) : (
                <div className="space-y-2" data-testid="my-phrases-list">
                  {phrases.map((p) => (
                    <PhraseRow key={p.phrase_id} p={p} onPractice={startPractice} onToggleSave={toggleSave} />
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Quiz */}
          <TabsContent value="quiz" className="mt-5">
            {packData && !isPro ? (
              <div className="bg-white border border-border rounded-2xl p-8 text-center" data-testid="quiz-locked">
                <div className="h-12 w-12 rounded-2xl bg-secondary grid place-items-center mx-auto mb-4"><Lock className="text-muted-foreground" /></div>
                <h3 className="font-head font-bold text-lg text-foreground">{t("quiz")} · {t("proFeature")}</h3>
                <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">{t("packsLockedBody")}</p>
                <button onClick={() => navigate("/plans")} data-testid="quiz-upgrade-btn" className="mt-5 h-11 px-6 rounded-xl bg-accent text-white font-medium hover:bg-accent/90">{t("upgradeNow")}</button>
              </div>
            ) : (
              <div className="bg-white border border-border rounded-2xl p-8 text-center" data-testid="quiz-panel">
                <div className="h-14 w-14 rounded-2xl bg-accent/10 text-accent grid place-items-center mx-auto mb-4"><Zap size={26} /></div>
                <h3 className="font-head font-bold text-lg text-foreground">{t("dailyQuiz")}</h3>
                <p className="text-sm text-muted-foreground mt-1 max-w-sm mx-auto">{t("quizEmpty")}</p>
                <button onClick={() => setQuizOpen(true)} data-testid="start-quiz-btn" className="mt-5 h-11 px-6 rounded-xl bg-accent text-white font-medium hover:bg-accent/90 inline-flex items-center gap-2"><Zap size={16} /> {t("startQuiz")}</button>
              </div>
            )}
          </TabsContent>

          {/* Role-play */}
          <TabsContent value="roleplay" className="mt-5">
            {packData && !isPro ? (
              <div className="bg-white border border-border rounded-2xl p-8 text-center" data-testid="roleplay-locked">
                <div className="h-12 w-12 rounded-2xl bg-secondary grid place-items-center mx-auto mb-4"><Lock className="text-muted-foreground" /></div>
                <h3 className="font-head font-bold text-lg text-foreground">{t("rolePlay")} · {t("proFeature")}</h3>
                <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">{t("packsLockedBody")}</p>
                <button onClick={() => navigate("/plans")} data-testid="roleplay-upgrade-btn" className="mt-5 h-11 px-6 rounded-xl bg-accent text-white font-medium hover:bg-accent/90">{t("upgradeNow")}</button>
              </div>
            ) : (
              <RolePlay />
            )}
          </TabsContent>

          {/* Content Packs */}
          <TabsContent value="packs" className="mt-5">
            {packData && !packData.pro ? (
              <div className="bg-white border border-border rounded-2xl p-8 text-center" data-testid="packs-locked">
                <div className="h-12 w-12 rounded-2xl bg-secondary grid place-items-center mx-auto mb-4"><Lock className="text-muted-foreground" /></div>
                <h3 className="font-head font-bold text-lg text-foreground">{t("packsLockedTitle")}</h3>
                <p className="text-sm text-muted-foreground mt-1 max-w-md mx-auto">{t("packsLockedBody")}</p>
                <button onClick={() => navigate("/plans")} data-testid="packs-upgrade-btn" className="mt-5 h-11 px-6 rounded-xl bg-accent text-white font-medium hover:bg-accent/90">{t("upgradeNow")}</button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 gap-4" data-testid="packs-grid">
                {packData?.packs?.map((pack) => (
                  <div key={pack.pack_id} className="bg-white border border-border rounded-2xl p-5" data-testid={`pack-${pack.pack_id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="text-3xl">{pack.icon}</span>
                        <div className="min-w-0">
                          <div className="font-head font-semibold text-foreground truncate">{pack.name}</div>
                          <div className="text-xs text-muted-foreground">{pack.phrases.length} {t("phrasesCount")}</div>
                        </div>
                      </div>
                      {pack.required && <Badge className="bg-primary/10 text-primary hover:bg-primary/10 border-0 shrink-0 text-[10px]">{t("requiredForTeam")}</Badge>}
                    </div>

                    <div className="flex items-center gap-2 mt-4">
                      <button
                        onClick={() => subscribe(pack)}
                        data-testid={`subscribe-${pack.pack_id}`}
                        className={`flex items-center gap-1.5 h-9 px-3 rounded-full text-sm font-medium border transition-colors ${pack.subscribed ? "bg-accent/10 text-accent border-accent/30" : "border-border hover:bg-secondary"}`}
                      >
                        {pack.subscribed ? <><Check size={15} /> {t("subscribedLabel")}</> : t("subscribeLabel")}
                      </button>
                      {isManager && (
                        <button
                          onClick={() => requirePack(pack)}
                          data-testid={`require-${pack.pack_id}`}
                          className={`flex items-center gap-1.5 h-9 px-3 rounded-full text-sm font-medium border transition-colors ${pack.required ? "bg-primary/10 text-primary border-primary/30" : "border-border hover:bg-secondary"}`}
                        >
                          <Users size={15} /> {pack.required ? t("requiredForTeam") : t("requireForTeam")}
                        </button>
                      )}
                      <button onClick={() => setExpanded(expanded === pack.pack_id ? null : pack.pack_id)} data-testid={`expand-${pack.pack_id}`} className="ms-auto text-sm text-primary font-medium hover:underline">
                        {expanded === pack.pack_id ? "−" : "+"}
                      </button>
                    </div>

                    {expanded === pack.pack_id && (
                      <div className="mt-3" data-testid={`pack-phrases-${pack.pack_id}`}>
                        {pack.phrases.map((ph, i) => <PackPhrase key={ph.translation || i} ph={ph} />)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <PracticeDialog
        phrase={practice}
        open={!!practice}
        onOpenChange={(o) => { if (!o) setPractice(null); }}
        onMastered={() => { loadStats(); loadPhrases(); }}
      />
      <QuizDialog open={quizOpen} onOpenChange={setQuizOpen} onDone={() => { loadStats(); loadPhrases(); }} />
    </div>
  );
}
